<template>
  <div class="masonry-layout" :style="containerStyle">
    <GalleryItem
      v-for="(item, index) in layout.items"
      :key="item.image.id"
      :image="item.image"
      :is-loaded="loadedImages[item.image.id]"
      :container-style="itemStyle(item)"
      :loading="index < columns * 4 ? 'eager' : 'lazy'"
      :fetch-priority="index < columns * 2 ? 'high' : 'auto'"
      img-mode="natural"
      v-bind="appearance"
      @click="$emit('item-click', $event)"
      @load="$emit('image-load', $event)"
    />
  </div>
</template>

<script>
import GalleryItem from '../GalleryItem.vue'
import { PHONE_MAX_HEIGHT_RATIO } from './phoneCap'

export default {
  name: 'MasonryLayout',

  components: { GalleryItem },

  props: {
    images: { type: Array, required: true },
    loadedImages: { type: Object, required: true },
    imageAspectRatios: { type: Object, required: true },
    columns: { type: Number, default: 3 },
    maxColumns: { type: Number, default: 3 },
    gap: { type: Number, default: 15 },
    containerWidth: { type: Number, default: 0 },
    rowHeight: { type: Number, default: 200 },
    appearance: { type: Object, default: () => ({}) }
  },

  emits: ['item-click', 'image-load'],

  computed: {
    columnWidth() {
      const width = this.containerWidth || this.columns * 400 + (this.columns - 1) * this.gap
      return Math.floor((width - (this.columns - 1) * this.gap) / this.columns)
    },

    containerStyle() {
      return {
        position: 'relative',
        height: this.layout.totalHeight + 'px'
      }
    },

    layout() {
      const colHeights = Array(this.columns).fill(0)
      const items = []

      for (const image of this.images) {
        // Place in the shortest column
        let col = 0
        for (let i = 1; i < this.columns; i++) {
          if (colHeights[i] < colHeights[col]) col = i
        }

        const ratio = this.imageAspectRatios[image.id]
        let itemWidth = this.columnWidth
        let itemHeight = ratio ? Math.round(this.columnWidth / ratio) : this.rowHeight
        // Phone: cap tall works and centre them
        const maxHeight = Math.round(this.columnWidth * PHONE_MAX_HEIGHT_RATIO)
        if (this.columns === 1 && ratio && itemHeight > maxHeight) {
          itemHeight = maxHeight
          itemWidth = Math.round(maxHeight * ratio)
        }

        items.push({
          image,
          top: colHeights[col],
          left: col * (this.columnWidth + this.gap) + (this.columnWidth - itemWidth) / 2,
          width: itemWidth,
          height: itemHeight
        })

        colHeights[col] += itemHeight + this.gap
      }

      const totalHeight = this.images.length
        ? Math.max(...colHeights) - this.gap
        : 0

      return { items, totalHeight: Math.max(0, totalHeight) }
    }
  },

  methods: {
    itemStyle(item) {
      const style = {
        position: 'absolute',
        top: item.top + 'px',
        left: item.left + 'px',
        width: item.width + 'px'
      }
      // Reserve space before image loads
      if (!this.loadedImages[item.image.id]) {
        const ratio = this.imageAspectRatios[item.image.id]
        if (ratio) {
          style.aspectRatio = String(ratio)
        } else {
          style.height = item.height + 'px'
        }
      }
      return style
    }
  }
}
</script>
